import { Params } from '@sentry/react/types/types';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { COLUMN_ACTION_GROUP } from '../../constants';
import { z } from 'zod/v3';
import { err, ok, Result } from 'neverthrow';
import { LlmColumnModel } from '../llm-column.model';
import {
  Text,
  toast,
  Loading,
  CollapsibleError,
} from '@cotera/client/app/components/ui';
import { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { useState } from 'react';
import { Assert, using } from '@cotera/utilities';
import { GraphModel } from '@cotera/client/v0/graph/graph.model';
import { DG } from '@cotera/sdk/core';
import { TC, Ty } from '@cotera/era';
import { ModalPageView } from '@cotera/client/app/components/app';
import React from 'react';
import { SetPrimaryKeyAction } from '@cotera/client/v0/dataset/actions/set-pk.action';
import { v4 } from 'uuid';
import { DatasetModel } from '@cotera/client/v0/dataset/model/dataset.model';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Label } from '@cotera/client/v0/components/ui/label';
import { DisposableModelContext } from '@cotera/client/v0/actions/etc';
import { Button } from '@cotera/client/v0/components/ui/button';
import { useTextFieldTracking } from '@cotera/client/app/etc';
import { findColumnNameConflict } from '../../utils/column-name-validation';
import { Routes } from '@cotera/client/v0/routes';

export class SaveLlmColumnAction extends BaseAction<
  {},
  { t: string; message?: string }[]
> {
  title = 'Save Column';
  inputSchema = z.object({});
  shortcut = ['ctrl', 's'];
  group = COLUMN_ACTION_GROUP;
  icon = 'check';

  constructor(
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      pop: () => void;
    },
    private readonly navigator: {
      pushAndReplace: (props: { route: string }) => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return (
      context.isInScope(GraphModel) &&
      context.hasTargetedModelOfType(LlmColumnModel) &&
      !context.targetedModelOfType(LlmColumnModel)!.hasErrors.snapshot()
    );
  }

  async execute(
    _: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(LlmColumnModel);
    const graph = context.getInScopeModelOfType(GraphModel);

    Assert.assert(model !== null);
    Assert.assert(graph !== null);

    // IMPORTANT: Capture ALL snapshots at once to avoid race conditions
    // where reactive state changes between reads would cause compilation errors
    const version = model.version.snapshot();
    const formState = model.formState.snapshot();
    const v2EditorState = model.v2EditorState.snapshot();
    const currentNameInGraph = model.currentNameInGraph.snapshot();
    const currentConfig = model.config.snapshot();
    const outputShapeV1 = model.outputShape.snapshot();
    const promptEraqlV1 = model.promptEraql.snapshot();

    // Get graph snapshot and build compiler from its commits to ensure consistency
    // Using graph.compiler.snapshot() separately can cause issues if staged operations
    // change between snapshots (e.g., from auto-publish)
    const graphSnapshot = graph.graph.snapshot();
    const compilerSnapshot = graphSnapshot.compiler;

    // Check for column name conflicts in current dataset and parent lineage
    const columnName = formState.name;
    const conflictDataset = findColumnNameConflict(
      graphSnapshot,
      model.datasetName,
      columnName,
      !model.isNew // Exclude current column if it's a rename (not new)
    );

    if (conflictDataset !== null) {
      return err([
        {
          t: 'error',
          message: `A column with the name "${columnName}" already exists in dataset "${conflictDataset}"`,
        },
      ]);
    }

    if (
      model.hasScheduleChanged() &&
      formState.when !== 'never' &&
      this.getExistingPk(model, graph) === null
    ) {
      const res = await this.askToSetPk(context, model, graph);

      if (res.isErr()) {
        return err(res.error);
      }
    }

    const makeOps = (): Result<
      DG.OperationData[],
      { t: string; message: string }
    > => {
      if (model.isNew) {
        // For new columns, use v2 format by default
        const editorStateObj = new DG.DocumentEditor.EditorState(v2EditorState);
        const outputTy = editorStateObj.getOutputTy();

        if (version === 'v2') {
          return ok([
            DG.Operations.createColumn({
              relName: model.datasetName,
              name: formState.name,
              config: {
                t: 'llm',
                version: 'v2',
                editorState: v2EditorState,
                outputTy: Ty.displayTy(outputTy),
                model: formState.model,
                schedule: formState.when,
                temperature: formState.temperature,
                maxOutputTokens: formState.maxOutputTokens,
              },
            }),
          ]);
        }

        return ok([
          DG.Operations.createColumn({
            relName: model.datasetName,
            name: formState.name,
            config: {
              t: 'llm',
              version: 'v2',
              editorState: v2EditorState,
              outputTy: Ty.displayTy(outputTy),
              model: formState.model,
              schedule: formState.when,
              temperature: formState.temperature,
              maxOutputTokens: formState.maxOutputTokens,
            },
          }),
        ]);
      }

      const operationsList: DG.OperationData[] = [];

      const columnBuildExplorer = graphSnapshot.getColumn({
        rel: model.datasetName,
        name: currentNameInGraph,
      });
      Assert.assert(columnBuildExplorer !== null);

      if (version === 'v2') {
        // Handle v2 config
        const editorStateObj = new DG.DocumentEditor.EditorState(v2EditorState);
        const outputTy = editorStateObj.getOutputTy();

        // Get existing config to preserve optional fields
        const existingV2Config =
          currentConfig &&
          'version' in currentConfig &&
          currentConfig.version === 'v2'
            ? currentConfig
            : null;

        const v2Config: DG.Graph.LlmColConfigV2 = {
          t: 'llm',
          version: 'v2',
          editorState: v2EditorState,
          outputTy: Ty.displayTy(outputTy),
          model: formState.model,
          schedule: formState.when,
          temperature: formState.temperature,
          maxOutputTokens: formState.maxOutputTokens,
          topP: existingV2Config?.topP,
          topK: existingV2Config?.topK,
          frequencyPenalty: existingV2Config?.frequencyPenalty,
          presencePenalty: existingV2Config?.presencePenalty,
        };

        const isTyCompatible = TC.isBackwardsCompatible({
          original: columnBuildExplorer.ty!,
          newType: outputTy,
        });

        const ops = isTyCompatible
          ? columnBuildExplorer.makeUpdateOps({
              config: v2Config,
            })
          : columnBuildExplorer.makeReplaceOps({
              config: v2Config,
            });

        if (ops.isErr()) {
          return err(ops.error);
        }

        operationsList.push(...ops.value);
      } else {
        // Handle v1 config
        Assert.assert(outputShapeV1 !== null);

        const isTyCompatible = TC.isBackwardsCompatible({
          original: columnBuildExplorer.ty!,
          newType: outputShapeV1,
        });

        const ops = isTyCompatible
          ? columnBuildExplorer.makeUpdateOps({
              config: {
                version: 'v1',
                t: 'llm',
                ...formState,
                prompt: promptEraqlV1,
                outputTy: Ty.displayTy(outputShapeV1),
              },
            })
          : columnBuildExplorer.makeReplaceOps({
              //if its a "breaking change" we need to replace the column
              config: {
                t: 'llm',
                version: 'v1',
                ...formState,
                prompt: promptEraqlV1,
                outputTy: Ty.displayTy(outputShapeV1),
              },
            });

        if (ops.isErr()) {
          return err(ops.error);
        }

        operationsList.push(...ops.value);
      }

      if (formState.name !== currentNameInGraph) {
        const renameOps = columnBuildExplorer.makeRenameOps({
          to: formState.name,
        });

        if (renameOps.isErr()) {
          return err({
            t: 'rename-failure',
            message: JSON.stringify(renameOps.error),
          });
        }

        operationsList.push(...renameOps.value);
      }

      return ok(operationsList);
    };

    const ops = makeOps();

    if (ops.isOk() && ops.value.map((x) => x.t).includes('replace-column')) {
      const res = await this.askToReplace(model);

      if (res === 'cancelled') {
        return err([{ t: 'cancelled' }]);
      }
    }

    if (ops.isErr()) {
      return err([{ t: 'error', message: JSON.stringify(ops.error) }]);
    }

    const tempCommit: DG.Commit = {
      actorId: 'system',
      timestamp: new Date(),
      operations: ops.value,
      message: null,
    };

    const compilationResult = compilerSnapshot.commit(tempCommit).compile();

    if (compilationResult.isErr()) {
      const errorMessage = compilationResult.error.prettyPrint();
      toast.error('Failed to compile graph', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([{ t: 'error', message: errorMessage }]);
    }

    const compiledSymbolResult =
      compilationResult.value.graph[model.datasetName]!;

    if (compiledSymbolResult.isErr()) {
      const errorMessage = compiledSymbolResult.error.prettyPrint();
      toast.error('Failed to compile column', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([{ t: 'error', message: errorMessage }]);
    }

    const stagingResult = await graph.attemptToStageOperations(ops.value);

    if (stagingResult.isErr()) {
      const errorMessage = stagingResult.error.prettyPrint();
      toast.error('Failed to stage operations', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([{ t: 'error', message: errorMessage }]);
    }

    toast.success('Changes saved');

    if (model.isNew) {
      this.navigator.pushAndReplace({
        route: `/dataset/${encodeURIComponent(model.datasetName)}/column/${
          formState.name
        }`,
      });
    }
    if (ops.value.map((x) => x.t).includes('rename-col')) {
      model.currentNameInGraph.set(formState.name);
      this.navigator.pushAndReplace({
        route: Routes.dataset
          .forId(model.datasetName)
          .column.edit(model.datasetName, formState.name)
          .toString(),
      });
    }

    return ok({});
  }

  shouldAsk(context: ApplicableContext): boolean {
    return context.targetedModelOfType(LlmColumnModel)?.isNew ?? false;
  }

  ask(_: {}, context: ExecuteContext): AskResponse<Params> {
    const model = context.targetedModelOfType(LlmColumnModel);

    Assert.assert(model !== null);

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => (
            <AskView
              onRespond={async (params) => {
                await onRespond(params);
                this.modalManager.pop();
              }}
              model={model}
            />
          ),
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }

  private async askToSetPk(
    context: ExecuteContext,
    model: LlmColumnModel,
    graph: GraphModel
  ) {
    const dataset = new DatasetModel(
      {
        name: model.datasetName,
        graph: graph.graph,
        compiler: graph.compiler,
      },
      {
        targeted: true,
        id: model.datasetName + v4(),
      }
    );

    const res = await using(
      new DisposableModelContext(context, dataset),
      async (_) => {
        return await context
          .getAction(SetPrimaryKeyAction)!
          .askOrExecute({}, context);
      }
    );

    if (res.isErr()) {
      return err(res.error);
    }

    if (res.value.cancelled) {
      return err([{ t: 'cancelled' }]);
    }

    return ok(this.getExistingPk(model, graph));
  }

  private getExistingPk(model: LlmColumnModel, graph: GraphModel) {
    const relSymbol = graph.graph.snapshot().forSymbol(model.datasetName);
    return (
      relSymbol?.builtNode.match(
        (r) => {
          Assert.assert(r.item.t === 'rel');
          return r.item.pk;
        },
        (_) => null
      ) ?? null
    );
  }

  private async askToReplace(model: LlmColumnModel) {
    return new Promise<'replace' | 'cancelled'>((resolve) => {
      this.modalManager.push({
        view: () => (
          <AskReplaceConfirmation
            onRespond={() => {
              resolve('replace');
            }}
            onCancel={() => {
              resolve('cancelled');
            }}
            model={model}
          />
        ),
      });
    }).finally(() => {
      this.modalManager.pop();
    });
  }
}

export const AskView: React.FC<{
  onRespond: (params: Params) => void;
  model: LlmColumnModel;
}> = ({ onRespond, model }) => {
  const newName = model.editorState.snapshot()?.title() ?? '';
  const [name, setName] = useState(newName);
  const [loading, setLoading] = useState(false);
  const trackChange = useTextFieldTracking('Column Name Typed');

  const relNode = model.inputs.graph
    .snapshot()
    .getRel({ symbol: model.datasetName });
  Assert.assert(relNode !== null);

  const graph = model.inputs.graph.snapshot();
  const conflictDataset = findColumnNameConflict(
    graph,
    model.datasetName,
    name,
    false
  );

  const validName = conflictDataset === null && name.trim().length > 0;

  return (
    <ModalPageView
      title={`Save ${model.currentNameInGraph.snapshot()}`}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex gap-2 items-center">
          <Label htmlFor="name" className="text-sm text-gray-600">
            Name
          </Label>
          <span className="text-xs text-destructive">
            {conflictDataset !== null &&
              `A column with this name already exists in dataset "${conflictDataset}"`}
          </span>
        </div>
        <Input
          autoFocus
          placeholder="name"
          value={name}
          onChange={(e) => {
            const newValue = e.target.value;
            setName(newValue);
            trackChange(newValue);
          }}
        />
        <div className="w-full flex justify-end">
          <Button
            type="submit"
            variant="default"
            disabled={!validName || loading}
            className="w-fit self-end"
            onClick={async () => {
              setLoading(true);
              React.startTransition(() => {
                model.formState.set({
                  ...model.formState.snapshot(),
                  name,
                });
                if (model.version.snapshot() === 'v2') {
                  model.v2EditorState.set({
                    ...model.editorState.snapshot()!.setTitle(name).getState(),
                  });
                }
                model.currentNameInGraph.set(name);
              });
              onRespond({ to: name });
              setLoading(false);
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Save'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};

export const AskReplaceConfirmation: React.FC<{
  onRespond: () => void;
  onCancel: () => void;
  model: LlmColumnModel;
}> = ({ onRespond, model, onCancel }) => {
  return (
    <ModalPageView
      title={`Replace ${model.currentNameInGraph.snapshot()}`}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <Text.P>
          This is a "breaking change", which will replace the existing column
          with the new one. It will trigger a backfill of the column.
          <br />
          Are you sure you want to continue?
        </Text.P>
        <div className="w-full flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={async () => {
              onRespond();
            }}
          >
            Replace
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
